import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Step A — edit a PERSONAL priority (Stage 1, structurally private). Edits the
 * fields the owner-only `personalPriorities.update` procedure supports: title,
 * description, priority. NO team/manager/sharing concept — this is private to
 * the owner and never leaves the personal workspace.
 */
export interface PersonalPriorityEditorValues {
  title: string;
  description: string;
  priority: number;
}

export interface PersonalPriorityEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: PersonalPriorityEditorValues;
  isAr: boolean;
  busy?: boolean;
  /** Optional dialog title override (e.g. Stage 3C "create for this" reuse).
   *  Defaults to the Step A edit copy; the private framing never changes. */
  heading?: string;
  /** Optional context line under the private framing — e.g. the create-for-this
   *  flow's "This will connect your personal priority to this team direction."
   *  Purely informational; the fields/options/validation stay IDENTICAL across
   *  every create/edit entry point (form-consistency contract). */
  contextNote?: string;
  onSubmit: (values: PersonalPriorityEditorValues) => void;
}

export function PersonalPriorityEditorDialog({
  open,
  onOpenChange,
  initial,
  isAr,
  busy,
  heading,
  contextNote,
  onSubmit,
}: PersonalPriorityEditorDialogProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [priority, setPriority] = useState(initial?.priority ?? 2);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? "");
      setDescription(initial?.description ?? "");
      setPriority(initial?.priority ?? 2);
    }
  }, [open, initial?.title, initial?.description, initial?.priority]);

  const canSubmit = title.trim().length > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="personal-priority-editor">
        <DialogHeader>
          <DialogTitle>
            {heading ?? t("Edit personal priority", "تعديل أولوية شخصية")}
          </DialogTitle>
          <DialogDescription>
            {t("Private to you. Only you can see this.", "خاصة بك. أنت وحدك تراها.")}
            {contextNote ? (
              <span className="block mt-1" data-testid="pp-editor-context">
                {contextNote}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="pp-title-input">{t("Title", "العنوان")}</Label>
            <Input
              id="pp-title-input"
              data-testid="pp-editor-title"
              value={title}
              maxLength={280}
              onChange={e => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pp-desc-input">{t("Description", "الوصف")}</Label>
            <Textarea
              id="pp-desc-input"
              data-testid="pp-editor-description"
              value={description}
              maxLength={2000}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            {/* UI label is "Importance" (glossary: High/Medium/Low = importance,
                never "priority" — that word means team/personal WORK items).
                The API field remains `priority`; no schema/API rename. */}
            <Label htmlFor="pp-priority-input">{t("Importance", "الأهمية")}</Label>
            <select
              id="pp-priority-input"
              data-testid="pp-editor-priority"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={priority}
              onChange={e => setPriority(Number(e.target.value))}
            >
              <option value={1}>{t("High", "عالية")}</option>
              <option value={2}>{t("Medium", "متوسطة")}</option>
              <option value={3}>{t("Low", "منخفضة")}</option>
            </select>
          </div>
        </div>

        {/* Clear breathing room between the last field (Importance) and the
            primary action, on top of the dialog's own gap — the buttons must
            never look glued to the select above (mobile-polish spacing). */}
        <DialogFooter className="mt-4" data-testid="pp-editor-footer">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="pp-editor-cancel"
          >
            {t("Cancel", "إلغاء")}
          </Button>
          <Button
            disabled={!canSubmit}
            data-testid="pp-editor-submit"
            onClick={() =>
              onSubmit({ title: title.trim(), description: description.trim(), priority })
            }
          >
            {t("Save", "حفظ")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
